import { useTranslation } from "react-i18next";
import useFeatureFlag from "./useFeatureFlag";
import { buildingStore } from "store/Building/BuildingStore";
import { UPCOMING_OPERATION_LIST } from "features/Building/consts.d";
import {
  IconApps,
  IconBolt,
  IconBusinessplan,
  IconChartArrowsVertical,
  IconChartBar,
  IconDatabaseSmile,
  IconDownload,
  IconLayoutColumns,
  IconLayoutDashboard,
  IconLeaf,
  IconList,
  IconRefresh,
  IconUpload,
} from "@tabler/icons-react";
import { IconBricks } from "utils";
import { MenuItemProps } from "features/Building/BuildingMenu/MenuItem";

const useBuildingTabOptions = () => {
  const { t } = useTranslation();
  const { isFeatureVisible } = useFeatureFlag();
  const iscircularityEnabled = isFeatureVisible("circularity_dashboard");
  const isHotspotAnalyzerEnabled = isFeatureVisible("hotspot_analyzer_tab");
  const integrationMenuEnabled = isFeatureVisible("integrations_menu");

  const isNotNewBuilding = () => {
    return (
      buildingStore.currentBuilding?.upcoming_operation !==
      UPCOMING_OPERATION_LIST[0].id
    );
  };

  function analyzeSubMenu() {
    return [
      {
        title: "DASHBOARDS",
        items: [
          {
            name: t("building.environmentalImpact"),
            path: "analytics",
            icon: <IconLeaf />,
            subtitle: t("building.environmentalImpactSubtitle"),
          },
          {
            name: t("building.materialValue"),
            path: "material-value",
            icon: <IconBusinessplan />,
            subtitle: t("building.materialValueSubtitle"),
          },
          ...(iscircularityEnabled
            ? [
                {
                  name: t("dashboards.circularity"),
                  path: "circularity",
                  icon: <IconRefresh />,
                  subtitle: t("building.circularitySubtitle"),
                },
              ]
            : []),
          {
            name: t("building.energy"),
            path: "energy",
            icon: <IconBolt />,
            subtitle: t("building.energySubtitle"),
          },

          {
            name: t("building.dataQuality"),
            path: "data-quality",
            icon: <IconDatabaseSmile />,
            subtitle: t("building.dataQualitySubtitle"),
          },
        ],
      },
      {
        title: "TOOLS",
        items: [
          {
            name: t("variants.title"),
            path: "variants",
            icon: <IconLayoutColumns />,
            subtitle: t("variants.subtitle"),
          },
          ...(isHotspotAnalyzerEnabled
            ? [
                {
                  name: "CO₂ Hotspot Analyzer",
                  path: "hotspot-analyzer",
                  icon: <IconChartArrowsVertical />,
                  subtitle: t("building.hotspotAnalyzerSubtitle"),
                },
              ]
            : []),
        ],
      },
    ];
  }

  const options: MenuItemProps[] = [
    {
      name: t("buildingPassport.passport"),
      path: "passport",
      icon: <IconLayoutDashboard />,
    },
    {
      name: t("buildingInventory.inventory"),
      path: "inventory",
      icon: <IconList />,
    },
    {
      name: t("dashboards.analytics"),
      icon: <IconChartBar />,
      subMenu: analyzeSubMenu(),
    },
    ...(buildingStore.currentBuilding?.exports_available
      ? [
          {
            name: t("exports.title"),
            path: "exports",
            icon: <IconDownload />,
          },
        ]
      : []),
    ...(integrationMenuEnabled
      ? [
          {
            name: t("building.integration"),
            icon: <IconApps />,
            subMenu: [
              {
                title: "",
                items: [
                  {
                    name: "eeule",
                    path: "eeule",
                    icon: <IconLayoutDashboard />,
                    subtitle: t("building.eEuleDescription"),
                  },
                ],
              },
            ],
          },
        ]
      : []),
    ...(isNotNewBuilding()
      ? [
          {
            name: t("commons.audit_material_sales"),
            path: "audit-material-sales",
            icon: IconBricks,
          },
        ]
      : []),
    ...(isFeatureVisible("ai_tab")
      ? [{ name: t("building.aiAssistant"), path: "ai" }]
      : []),
  ];

  const rightOptions: MenuItemProps[] = [
    {
      name: t("buildingPassport.sourceFiles"),
      path: "source-files",
      icon: <IconUpload />,
    },
  ];

  return { options, rightOptions };
};

export default useBuildingTabOptions;
